import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { TermBlock, TermDetail } from "@termclear/contracts";
import { playUiSound } from "../ui-sound";
import { TermTitle } from "./TermTitle";

const BlockContent = lazy(() => import("./MarkdownContent"));

export function TermDetailView(props: {
  term: TermDetail;
  reviewMode: boolean;
  position: number | null;
  total: number;
  onBack: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onRating: (rating: number) => Promise<void>;
  onChat: (block: TermBlock) => void;
  onMarker: (block: TermBlock) => void;
}) {
  const [revealed, setRevealed] = useState(!props.reviewMode);
  const touch = useRef<{ x: number; y: number; edge: boolean } | null>(null);

  function touchStart(event: React.TouchEvent) {
    const point = event.touches[0];
    if (!point) return;
    const target = event.target;
    if (target instanceof Element && target.closest(
      "button, a, input, textarea, select, [data-horizontal-scroll], .katex-display, table, .rating-rail",
    )) {
      touch.current = null;
      return;
    }
    touch.current = {
      x: point.clientX,
      y: point.clientY,
      edge: point.clientX < 28 || point.clientX > window.innerWidth - 28,
    };
  }

  function touchEnd(event: React.TouchEvent) {
    const start = touch.current;
    const point = event.changedTouches[0];
    touch.current = null;
    if (!start || !point || start.edge) return;
    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    if (Math.abs(dx) < 64 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    if (dx < 0) props.onNext();
    else props.onPrevious();
  }

  return (
    <section className="detail-view view">
      <div className="session-bar">
        <div>
          <span className="mode-label">{props.reviewMode ? "浏览与复习" : "普通浏览"}</span>
          <span className="session-progress">{props.position ?? "—"} / {props.total}</span>
        </div>
        <div className="session-actions">
          <button className="text-button" type="button" onClick={props.onPrevious}>上一条</button>
          <button className="text-button" type="button" onClick={props.onNext}>下一条</button>
        </div>
      </div>
      <article
        className="term-article"
        onTouchStart={touchStart}
        onTouchEnd={touchEnd}
        onTouchCancel={() => { touch.current = null; }}
      >
        <header className="term-heading">
          <p className="term-meta">{props.term.category} · 当前修订 v{props.term.currentRevision.version}</p>
          <h1><TermTitle name={props.term.name} format={props.term.titleFormat} /></h1>
        </header>

        <RatingRail value={props.term.needUnderstanding} onChange={props.onRating} />

        <div className={`answer-wrap ${props.reviewMode && !revealed ? "is-masked" : ""}`.trim()}>
          {props.reviewMode && !revealed ? (
            <button className="answer-mask" type="button" onClick={() => setRevealed(true)}>
              <span>点一下查看解释</span>
              <small>答案区域会完整揭示</small>
            </button>
          ) : null}
          <div className="understanding-thread" aria-hidden={props.reviewMode && !revealed}>
            {props.term.blocks.map((block) => (
              <section className="content-block" key={block.id} data-block-id={block.id}>
                <span className="thread-node" aria-hidden="true" />
                <div className="block-heading">
                  <h2>{block.title}</h2>
                  <div className="block-actions">
                    <button type="button" onClick={() => props.onChat(block)}>这段没懂</button>
                    <button type="button" onClick={() => props.onMarker(block)}>标记一下</button>
                  </div>
                </div>
                <Suspense fallback={<p className="muted">正在准备代码与公式渲染…</p>}>
                  <BlockContent block={block} />
                </Suspense>
                {block.clarifications.length > 0 ? (
                  <section className="clarification-list" aria-label="从对话中补清的疑问">
                    <h3>从对话中补清的疑问</h3>
                    {block.clarifications.map((clarification) => (
                      <article className="clarification-item" key={clarification.id}>
                        <blockquote>“{clarification.question}”</blockquote>
                        <p>{clarification.answer}</p>
                        <small>应用于 v{clarification.appliedRevisionVersion}</small>
                      </article>
                    ))}
                  </section>
                ) : null}
              </section>
            ))}
          </div>
        </div>

        <footer className="term-footer">
          <button className="secondary-button" type="button" onClick={props.onPrevious}>← 上一条</button>
          <p>也可以在正文安全区域左右滑动</p>
          <button className="secondary-button" type="button" onClick={props.onNext}>下一条 →</button>
        </footer>
      </article>
      <button className="mobile-back" type="button" onClick={props.onBack}>返回词库</button>
    </section>
  );
}

function RatingRail(props: { value: number; onChange: (value: number) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState<RatingRailPosition>(readRatingRailPosition);
  const rail = useRef<HTMLDivElement>(null);
  const drag = useRef<RatingRailDrag | null>(null);
  const suppressClickUntil = useRef(0);

  useEffect(() => {
    const clampPosition = () => {
      const element = rail.current;
      if (!element) return;
      setPosition((current) => {
        const next = { ...current, top: clampRatingRailTop(current.top, element.offsetHeight) };
        if (next.top !== current.top) writeRatingRailPosition(next);
        return next.top === current.top ? current : next;
      });
    };
    clampPosition();
    window.addEventListener("resize", clampPosition);
    window.visualViewport?.addEventListener("resize", clampPosition);
    return () => {
      window.removeEventListener("resize", clampPosition);
      window.visualViewport?.removeEventListener("resize", clampPosition);
    };
  }, []);

  useEffect(() => {
    const moveFromWindow = (event: PointerEvent) => {
      movePointer(event.pointerId, event.clientX, event.clientY, () => event.preventDefault());
    };
    const finishFromWindow = (event: PointerEvent) => finishPointer(event.pointerId);
    const finishOnBlur = () => {
      if (drag.current) finishPointer(drag.current.pointerId);
    };
    window.addEventListener("pointermove", moveFromWindow, { passive: false });
    window.addEventListener("pointerup", finishFromWindow);
    window.addEventListener("pointercancel", finishFromWindow);
    window.addEventListener("blur", finishOnBlur);
    return () => {
      window.removeEventListener("pointermove", moveFromWindow);
      window.removeEventListener("pointerup", finishFromWindow);
      window.removeEventListener("pointercancel", finishFromWindow);
      window.removeEventListener("blur", finishOnBlur);
    };
  }, []);

  async function change(value: number) {
    if (performance.now() < suppressClickUntil.current) return;
    setBusy(true);
    try {
      await props.onChange(value);
      void playUiSound("rating");
    } catch {
      // The parent restores the previous rating and reports the request error.
    } finally {
      setBusy(false);
    }
  }

  function pointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    drag.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    element.dataset.dragging = "true";
  }

  function movePointer(
    pointerId: number,
    clientX: number,
    clientY: number,
    preventDefault?: () => void,
  ) {
    const current = drag.current;
    if (!current || current.pointerId !== pointerId) return;
    if (!current.moved && Math.hypot(clientX - current.startX, clientY - current.startY) < 8) return;
    current.moved = true;
    preventDefault?.();
    const element = rail.current;
    if (!element) return;
    const edge = 8;
    const left = Math.min(
      Math.max(edge, clientX - current.offsetX),
      Math.max(edge, window.innerWidth - element.offsetWidth - edge),
    );
    const top = clampRatingRailTop(clientY - current.offsetY, element.offsetHeight);
    element.style.left = `${left}px`;
    element.style.right = "auto";
    element.style.top = `${top}px`;
  }

  function finishPointer(pointerId: number) {
    const current = drag.current;
    if (!current || current.pointerId !== pointerId) return;
    const element = rail.current;
    if (!element) return;
    drag.current = null;
    delete element.dataset.dragging;
    if (!current.moved) return;
    suppressClickUntil.current = performance.now() + 350;
    const rect = element.getBoundingClientRect();
    const next: RatingRailPosition = {
      side: rect.left + rect.width / 2 < window.innerWidth / 2 ? "left" : "right",
      top: clampRatingRailTop(rect.top, rect.height),
    };
    element.style.removeProperty("left");
    element.style.removeProperty("right");
    element.style.top = `${next.top}px`;
    setPosition(next);
    writeRatingRailPosition(next);
  }

  return (
    <div
      className={`rating-rail is-${position.side}`}
      role="radiogroup"
      aria-label="还需理解程度；可以拖动到屏幕左侧或右侧"
      ref={rail}
      style={{ top: position.top }}
      onPointerDown={pointerDown}
    >
      <div className="rating-stars">
        {[5, 4, 3, 2, 1].map((rating) => (
          <button
            className={rating <= props.value ? "is-filled" : "is-empty"}
            type="button"
            role="radio"
            aria-checked={props.value === rating}
            aria-label={`需理解 ${rating} 星`}
            disabled={busy}
            key={rating}
            onClick={() => void change(rating)}
          >
            {rating <= props.value ? "★" : "☆"}
          </button>
        ))}
      </div>
      <output>{props.value} / 5</output>
    </div>
  );
}

type RatingRailSide = "left" | "right";

interface RatingRailPosition {
  side: RatingRailSide;
  top: number;
}

interface RatingRailDrag {
  pointerId: number;
  offsetX: number;
  offsetY: number;
  startX: number;
  startY: number;
  moved: boolean;
}

const ratingRailStorageKey = "termclear-rating-rail-v1";

function readRatingRailPosition(): RatingRailPosition {
  if (typeof window === "undefined") return { side: "right", top: 142 };
  try {
    const value = JSON.parse(window.localStorage.getItem(ratingRailStorageKey) ?? "null") as unknown;
    if (isRatingRailPosition(value)) return value;
  } catch {
    // A blocked or malformed local preference falls back to the default dock.
  }
  return { side: "right", top: window.innerWidth <= 420 ? 120 : 142 };
}

function writeRatingRailPosition(position: RatingRailPosition): void {
  try {
    window.localStorage.setItem(ratingRailStorageKey, JSON.stringify(position));
  } catch {
    // The drag still works for this page when localStorage is unavailable.
  }
}

function isRatingRailPosition(value: unknown): value is RatingRailPosition {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (candidate.side === "left" || candidate.side === "right")
    && typeof candidate.top === "number"
    && Number.isFinite(candidate.top);
}

function clampRatingRailTop(value: number, height: number): number {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const minimum = window.innerWidth <= 420 ? 120 : 132;
  const maximum = Math.max(minimum, viewportHeight - height - 12);
  return Math.round(Math.min(Math.max(value, minimum), maximum));
}
